import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, ShieldAlert, Timer, Trash2, UserMinus, UserPlus, Users } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { GatedButton } from '@/components/ui/GatedButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { EmptyState, Skeleton, Spinner } from '@/components/ui/Feedback';
import { InviteTechnicianDialog } from './InviteTechnicianDialog';
import { AssignRoleDialog } from './AssignRoleDialog';
import {
  deleteRoleAssignment,
  listRoles,
  listTechnicians,
  setTechnicianStatus,
} from '@/lib/api/endpoints/technicians';
import { useAuth } from '@/lib/auth/AuthProvider';
import { queryKeys } from '@/lib/query-keys';
import { formatAbsolute, formatRelative, initials } from '@/lib/format';
import {
  DANGEROUS_PERMISSIONS,
  type RoleAssignment,
  type RoleAssignmentScope,
  type Technician,
  type UserStatus,
} from '@/lib/api/types';

/*
  Technicians, roles, and the management actions for both.

  The scope of each assignment is spelled out rather than summarised, because "Technician on
  Acme Dental" and "Technician on Acme Dental / Main Clinic" are different grants and the
  difference is the entire point of the model. A row that said only "Technician" would hide the
  part that matters.

  Permissions flagged dangerous are marked in the role table. In the API they additionally require
  a step-up MFA assertion within the last five minutes; the badge here is a reading aid, and the
  console has no ability to relax it.

  Management, all behind technician.manage:

  * Invite, which creates the account and its first assignment in one step.
  * Grant an additional role at an additional scope.
  * Remove an assignment. Destructive, because it takes access away, so it is confirmed and it
    names the role and the scope in the copy. The API refuses to remove someone's last
    assignment, since an account that can sign in and see nothing reads as a broken console
    rather than as a revoked user; suspending is the honest way to say "not any more".
  * Suspend or reinstate. Also confirmed. The API refuses to let you suspend yourself, so that
    nobody can lock the tenant out with one click.

  The signed-in user's own assignments are the input to the no-escalation check in the invite and
  grant dialogs, so they are read out of the technician list rather than from
  CurrentUser.permissions. That field is flattened across scopes and would answer the escalation
  question wrongly; the reasoning is written out at the top of ./effective-permissions.ts.
*/

const STATUS_TONE: Readonly<Record<UserStatus, BadgeTone>> = {
  active: 'success',
  invited: 'info',
  suspended: 'warning',
  disabled: 'neutral',
};

function scopeLabel(scope: RoleAssignmentScope): string {
  switch (scope.kind) {
    case 'tenant':
      return 'Tenant wide';
    case 'organization':
      return `Organization: ${scope.label}`;
    case 'site':
      return `Site: ${scope.label}`;
    case 'device_group':
      return `Device group: ${scope.label}`;
    case 'device':
      return `Single device: ${scope.label}`;
  }
}

type DialogState =
  | { readonly kind: 'none' }
  | { readonly kind: 'invite' }
  | { readonly kind: 'assign'; readonly technician: Technician }
  | {
      readonly kind: 'removeAssignment';
      readonly technician: Technician;
      readonly assignment: RoleAssignment;
    }
  | {
      readonly kind: 'status';
      readonly technician: Technician;
      readonly next: UserStatus;
    };

const MANAGE_REASON =
  'Inviting, suspending and changing role assignments needs the technician.manage permission, which none of your role assignments grant. It is marked dangerous in the permission catalog, because it is the permission that can be used to hand out every other one.';

export function TechniciansPage(): React.JSX.Element {
  const { can, user } = useAuth();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });

  const technicians = useQuery({
    queryKey: queryKeys.technicians,
    queryFn: listTechnicians,
  });
  const roles = useQuery({ queryKey: queryKeys.roles, queryFn: listRoles });

  const manage = can('technician.manage');

  // The signed-in user's own assignments, with their scopes intact. See the note above.
  const grantorAssignments: readonly RoleAssignment[] =
    (technicians.data ?? []).find((tech) => tech.id === user?.id)?.assignments ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.technicians });
    void queryClient.invalidateQueries({ queryKey: queryKeys.auditEvents('') });
  };

  const removeAssignment = useMutation({
    mutationFn: (assignmentId: string) => deleteRoleAssignment(assignmentId),
    onSuccess: invalidate,
  });
  const changeStatus = useMutation({
    mutationFn: (input: { readonly userId: string; readonly status: UserStatus }) =>
      setTechnicianStatus(input.userId, input.status),
    onSuccess: invalidate,
  });

  function close(): void {
    setDialog({ kind: 'none' });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Technicians"
        description="Users, the roles they hold, and the exact scope each role is granted on."
        actions={
          manage ? (
            <Button
              variant="primary"
              onClick={() => {
                setDialog({ kind: 'invite' });
              }}
            >
              <UserPlus className="size-4" aria-hidden="true" />
              Invite technician
            </Button>
          ) : (
            <GatedButton reason={MANAGE_REASON}>
              <UserPlus className="size-4" aria-hidden="true" />
              Invite technician
            </GatedButton>
          )
        }
      />

      {technicians.isError ? (
        <ApiErrorAlert
          error={technicians.error}
          context="Could not load technicians"
          onRetry={() => {
            void technicians.refetch();
          }}
        />
      ) : null}

      <Card>
        <CardHeader
          title="People"
          description="An assignment grants its permissions at that node and everything below it. There are no deny rules."
        />
        {technicians.isPending ? (
          <div className="flex flex-col gap-2 p-4">
            <Spinner label="Loading technicians" />
            {[0, 1, 2].map((index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        ) : (technicians.data ?? []).length === 0 ? (
          <EmptyState
            icon={<Users className="size-5" aria-hidden="true" />}
            title="No technicians yet"
            description="Invite a user, then grant them a role at the narrowest scope that lets them do the job."
            action={
              manage ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialog({ kind: 'invite' });
                  }}
                >
                  <UserPlus className="size-4" aria-hidden="true" />
                  Invite technician
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul>
            {(technicians.data ?? []).map((tech) => {
              const isSelf = tech.id === user?.id;
              return (
                <li
                  key={tech.id}
                  className="flex flex-wrap items-start gap-4 border-b border-border px-4 py-3 last:border-b-0"
                >
                  <span
                    aria-hidden="true"
                    className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-bold text-accent-soft-fg"
                  >
                    {initials(tech.fullName)}
                  </span>

                  <div className="min-w-48 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-fg">{tech.fullName}</span>
                      {isSelf ? <Badge tone="accent">You</Badge> : null}
                      <Badge tone={STATUS_TONE[tech.status]}>{tech.status}</Badge>
                      {tech.mfaMethods.length === 0 ? (
                        <Badge tone="warning" title="No second factor enrolled yet">
                          <ShieldAlert className="size-3" aria-hidden="true" />
                          MFA not set up
                        </Badge>
                      ) : (
                        <Badge tone="neutral" title="Second factors enrolled">
                          <KeyRound className="size-3" aria-hidden="true" />
                          {tech.mfaMethods.join(', ')}
                        </Badge>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-fg-muted">{tech.email}</p>
                    <p className="mt-0.5 text-xs text-fg-subtle">
                      {tech.lastLoginAt === null ? (
                        'Has never signed in'
                      ) : (
                        <>
                          Last signed in{' '}
                          <time dateTime={tech.lastLoginAt} title={formatAbsolute(tech.lastLoginAt)}>
                            {formatRelative(tech.lastLoginAt)}
                          </time>
                        </>
                      )}
                    </p>

                    {manage ? (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setDialog({ kind: 'assign', technician: tech });
                          }}
                        >
                          <Plus className="size-3.5" aria-hidden="true" />
                          Grant role
                        </Button>
                        {tech.status === 'suspended' || tech.status === 'disabled' ? (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setDialog({ kind: 'status', technician: tech, next: 'active' });
                            }}
                          >
                            Reinstate
                          </Button>
                        ) : (
                          <Button
                            variant="secondary"
                            size="sm"
                            disabled={isSelf}
                            title={
                              isSelf
                                ? 'You cannot suspend your own account. Ask another administrator.'
                                : undefined
                            }
                            onClick={() => {
                              setDialog({ kind: 'status', technician: tech, next: 'suspended' });
                            }}
                          >
                            <UserMinus className="size-3.5" aria-hidden="true" />
                            Suspend
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </div>

                  <div className="min-w-64 flex-1">
                    <h3 className="mb-1.5 text-xs font-semibold tracking-wide text-fg-muted uppercase">
                      Assignments
                    </h3>
                    <ul className="flex flex-col gap-1.5">
                      {tech.assignments.map((item) => (
                        <li key={item.id} className="flex items-start gap-1.5 text-xs">
                          <span className="min-w-0 flex-1">
                            <span className="font-semibold text-fg">{item.roleName}</span>
                            <span className="text-fg-muted"> on {scopeLabel(item.scope)}</span>
                            {item.expiresAt !== null ? (
                              <span className="ml-1.5 inline-flex items-center gap-1 text-[var(--color-warning)]">
                                <Timer className="size-3" aria-hidden="true" />
                                expires{' '}
                                <time
                                  dateTime={item.expiresAt}
                                  title={formatAbsolute(item.expiresAt)}
                                >
                                  {formatRelative(item.expiresAt)}
                                </time>
                              </span>
                            ) : null}
                          </span>
                          {manage ? (
                            <button
                              type="button"
                              aria-label={`Remove ${item.roleName} on ${scopeLabel(item.scope)} from ${tech.fullName}`}
                              onClick={() => {
                                setDialog({
                                  kind: 'removeAssignment',
                                  technician: tech,
                                  assignment: item,
                                });
                              }}
                              className="shrink-0 rounded p-0.5 text-fg-subtle hover:bg-surface-hover hover:text-[var(--color-danger)]"
                            >
                              <Trash2 className="size-3.5" aria-hidden="true" />
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Roles"
          description="Built-in roles cannot be edited. Custom roles cannot grant a permission the author does not already hold at that scope."
        />
        {roles.isPending ? (
          <div className="p-4">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] border-collapse text-left text-sm">
              <caption className="sr-only">
                Roles and the permissions each one bundles. Permissions marked dangerous
                additionally require a fresh second factor at the API.
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  <th scope="col" className="px-4 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
                    Role
                  </th>
                  <th scope="col" className="px-4 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
                    Type
                  </th>
                  <th scope="col" className="px-4 py-2 text-xs font-semibold tracking-wide text-fg-muted uppercase">
                    Permissions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(roles.data ?? []).map((role) => (
                  <tr key={role.id} className="border-b border-border last:border-b-0">
                    <th scope="row" className="max-w-56 px-4 py-3 align-top font-normal">
                      <span className="block text-sm font-semibold text-fg">{role.name}</span>
                      <span className="block font-mono text-[11px] text-fg-subtle">{role.key}</span>
                      {role.description !== null ? (
                        <span className="mt-1 block text-xs text-fg-muted">{role.description}</span>
                      ) : null}
                    </th>
                    <td className="px-4 py-3 align-top">
                      <Badge tone={role.isSystem ? 'neutral' : 'accent'}>
                        {role.isSystem ? 'Built in' : 'Custom'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="flex flex-wrap gap-1">
                        {role.permissions.map((permission) => {
                          const dangerous = DANGEROUS_PERMISSIONS.includes(permission);
                          return (
                            <Badge
                              key={permission}
                              tone={dangerous ? 'danger' : 'neutral'}
                              title={
                                dangerous
                                  ? 'Dangerous: requires a second confirmation and a fresh MFA assertion'
                                  : undefined
                              }
                            >
                              <span className="font-mono text-[11px]">{permission}</span>
                            </Badge>
                          );
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dialog.kind === 'invite' ? (
        <InviteTechnicianDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          grantorAssignments={grantorAssignments}
        />
      ) : null}

      {dialog.kind === 'assign' ? (
        <AssignRoleDialog
          key={dialog.technician.id}
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          technician={dialog.technician}
          grantorAssignments={grantorAssignments}
        />
      ) : null}

      {dialog.kind === 'removeAssignment' ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          title="Remove this role assignment?"
          summary={`${dialog.assignment.roleName} on ${scopeLabel(dialog.assignment.scope)}, held by ${dialog.technician.fullName}.`}
          consequence={
            <>
              <p>
                {dialog.technician.fullName} immediately stops holding{' '}
                {dialog.assignment.roleName} on{' '}
                {scopeLabel(dialog.assignment.scope).toLowerCase()} and everything below it. Their
                other assignments are untouched, so they may still reach some of the same machines
                through one of those.
              </p>
              <p className="mt-1">
                Permissions are resolved server side on every request, so this takes effect on
                their next API call rather than when their token next refreshes. A session they are
                in the middle of is re-checked as well.
              </p>
              {dialog.technician.assignments.length === 1 ? (
                <p className="mt-1">
                  This is their only assignment. The API will refuse to remove it, because an
                  account that can sign in and see nothing looks like a broken console. Suspend the
                  account instead.
                </p>
              ) : null}
            </>
          }
          confirmLabel="Remove assignment"
          tone="danger"
          onConfirm={() => removeAssignment.mutateAsync(dialog.assignment.id)}
        />
      ) : null}

      {dialog.kind === 'status' ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          title={
            dialog.next === 'active'
              ? `Reinstate ${dialog.technician.fullName}?`
              : `Suspend ${dialog.technician.fullName}?`
          }
          summary={`${dialog.technician.fullName}, ${dialog.technician.email}. Currently ${dialog.technician.status}.`}
          consequence={
            dialog.next === 'active' ? (
              <p>
                {dialog.technician.fullName} can sign in again, and every role assignment they still
                hold starts granting again. Nothing was removed while they were suspended, so check
                their assignments are still the ones you want before reinstating.
              </p>
            ) : (
              <>
                <p>
                  {dialog.technician.fullName} can no longer sign in, and their existing browser
                  sessions are revoked. Any remote session they are running ends.
                </p>
                <p className="mt-1">
                  Their role assignments are kept, so reinstating restores exactly the access they
                  had. If the intent is to take access away permanently, remove the assignments as
                  well.
                </p>
              </>
            )
          }
          confirmLabel={dialog.next === 'active' ? 'Reinstate account' : 'Suspend account'}
          tone={dialog.next === 'active' ? 'warning' : 'danger'}
          onConfirm={() =>
            changeStatus.mutateAsync({ userId: dialog.technician.id, status: dialog.next })
          }
        />
      ) : null}
    </div>
  );
}
